import React, { memo, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import dynamic from 'next/dynamic';

// actions
import { useFeatures } from '@atc/features';

import remoteActions from '@/actions/srp/remoteActions';

import {
    srpFiltersDuck,
    srpPaginationDuck,
    srpResultsDuck,
    srpSortDuck,
    srpSupplementalDuck,
} from '@/ducks/srp';

import LazyComponent from '@/components/LazyComponent';

import InventoryTransitionCardContainer from './InventoryTransitionCardContainer';
import SupplementalSearchPlaceholder from './SupplementalSearchPlaceholder';

const InventorySupplementalResultsContainer = dynamic(() => import(/* webpackChunkName: "InventorySupplementalResultsContainer" */ './InventorySupplementalResultsContainer'), { ssr: false, loading: () => (<SupplementalSearchPlaceholder />) });

function SupplementalSearchContainer({
    showSupplementalResults,
    isLastPage,
}) {
    const dispatch = useDispatch();

    const resultCount = useSelector(srpResultsDuck.selectors.getResultsCount);
    const totalSupplementalCount = useSelector(srpSupplementalDuck.selectors.getResultCount);
    const sort = useSelector(srpSortDuck.selectors.getDuckState) || '';
    const numRecords = useSelector(srpPaginationDuck.selectors.getNumRecords);
    const searchRadius = useSelector(srpFiltersDuck.selectors.getSelectedSearchRadius);

    const currentLoadedCount = useSelector(srpSupplementalDuck.selectors.getLoadedCount);

    const filtersValues = useSelector(srpFiltersDuck.selectors.getFiltersValues);

    const {
        brand: [, { channel, inventory_image_url: inventoryImageUrl }],
        standard_listing_phone_display: [displayPhoneNumber],
        standard_listing_owner_name: [hideOwnerName],
    } = useFeatures([
        'brand',
        'standard_listing_phone_display',
        'standard_listing_owner_name',
    ]);

    /*
     * Purpose:  This hook will re-request supplemental results when showSupplementalResults changes as a result of:
     *                     - The # of the listings on the page changing, or the
     *                     - The # of listings the user wants to view at a time on a page changing, or the
     *                     - The search radius changes via the user
     */
    useEffect(() => {
        /* Clears supplemental redux and component state whenever the user triggers an SRP change.
         * This prevents loading stale supplemental results as well as ensures impression firing is accurate.
         */
        if (totalSupplementalCount > 0) {
            // TODO we should move this into the actual filter callback instead of some crazy effect.  We have to check
            // for total count here otherwise we dispatch a new empty array on every page load which causes state updates
            // and renderings that aren't necessary.
            dispatch(srpSupplementalDuck.creators.clear());
        }

        if (showSupplementalResults) {
            // fetch new results if we meet the criteria in showSupplementalResults
            dispatch(remoteActions.getSupplementalResultsData({
                channel,
                displayPhoneNumber,
                firstRecord: '0',
                hideOwnerName,
                inventoryImageUrl,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showSupplementalResults, filtersValues, numRecords, sort]);

    const renderListingChunks = (startIndex, endIndex) => totalSupplementalCount >= startIndex && (
        <LazyComponent
            key={`supplemental-${startIndex}`}
            fallback={currentLoadedCount <= startIndex && <SupplementalSearchPlaceholder />}
        >
            <InventorySupplementalResultsContainer
                startIndex={startIndex}
                endIndex={endIndex}
            />
        </LazyComponent>
    );

    return totalSupplementalCount > 0 && (
        <>
            {/* Only show TransitionCard when regular results are limited */}
            {resultCount <= 15 && (
                <InventoryTransitionCardContainer
                    key="invSupplementalTransitionCard"
                    searchRadius={searchRadius}
                    isLastPage={isLastPage}
                />
            )}
            <>
                {renderListingChunks(0, 25)}
                {renderListingChunks(25, 50)}
                {renderListingChunks(50, 75)}
                {renderListingChunks(75, 100)}
            </>
        </>
    );
}

export default memo(SupplementalSearchContainer);
